import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    ArrowRight,
    Flame,
    Sparkles,
    TrendingUp,
    Zap,
    Check,
    Target,
    LineChart,
    Layers,
    Mic,
    Video,
    Quote,
    ChevronDown,
    Clock,
} from "lucide-react";
import PageNav from "@/components/PageNav";
import DigtekNavbar from "@/components/digtek/DigtekNavbar";
import DigtekFooter from "@/components/digtek/DigtekFooter";
import Footer from "@/components/Footer";

/**
 * Growth services page — served at /services/paid-media (URL kept for
 * SEO/sitemap continuity). Eleven services grouped in three categories:
 * Growth (paid/creator), Reputation (PR/awards/founder brand), and
 * Content (podcast/documentary/case studies). Each service has two tier
 * options unless the format is inherently one-shot (docs, research).
 */
const services = [
    {
        id: "ugc-ads",
        icon: Sparkles,
        eyebrow: "01",
        category: "growth",
        name: "UGC Ads",
        tagline: "Stop burning studio budgets on ads no one watches.",
        description:
            "Real creators, native hooks, 3× the scroll-stop rate at a third of the cost of studio-shot work. We source, brief and direct actual customers (not actors) to make ads that convert on the first 1.2 seconds. Ships every week. Rights-cleared for 12 months.",
        salesExample: {
            before: "Watch our 60-second brand video on YouTube.",
            after: "Watch this 30-second phone-shot ad from an actual customer explaining exactly why they switched — 3.2M people already have.",
        },
        valueTagline: "Turn every phone camera into a conversion asset your competitors can't afford to copy.",
        deliverables: [
            "Creator sourcing + rights-cleared briefs",
            "3 shipped ad variations per concept",
            "Scroll-stop hook A/B tested per concept",
            "Native captions + subtitles per platform",
            "Full commercial usage rights, 12 months",
        ],
        tiers: [
            {
                id: "start",
                name: "Start",
                price: "$2,499",
                cadence: "/ mo",
                extras: "6 UGC ads / month",
                positioning: "Prove creator-shot ads outperform your studio work — in 30 days, with data.",
                timeToLive: "First concept live in 7 days",
                highlight: false,
            },
            {
                id: "scale",
                name: "Scale",
                price: "$5,499",
                cadence: "/ mo",
                extras: "16 UGC ads / month · dedicated director",
                positioning: "Turn creator-shot ads into your highest-ROAS acquisition channel.",
                timeToLive: "First shipped concept in 5 days",
                highlight: true,
            },
        ],
    },
    {
        id: "paid-promotions",
        icon: TrendingUp,
        eyebrow: "02",
        category: "growth",
        name: "Paid Promotions",
        tagline: "Turn every ad dollar into a documented model of your buyer.",
        description:
            "Most agencies spend your budget and hand you a ROAS number. We spend it, keep the answers, and by month three you have a written map of exactly who buys, why, and what phrase makes them lean in. Fixed fee. Never a percentage of your spend.",
        salesExample: {
            before: "We think our ideal customer is B2B mid-market — would you like a demo?",
            after: "Our last 90 days of paid data shows CFOs at Series B companies with 40+ employees convert at 4.2× the average. That's you. Here's why.",
        },
        valueTagline: "Build a compounding buyer-intelligence engine — every dollar you spend now makes the next one cheaper.",
        deliverables: [
            "1–4 channels + retargeting stack",
            "Weekly creative research cohort (hypothesis-labelled)",
            "Pixel + conversion API + attribution stitching",
            "Landing page CRO — 2 tests / week at Scale",
            "Weekly Loom + written retro monthly",
            "Slack channel · 4h SLA at Scale",
        ],
        tiers: [
            {
                id: "spark",
                name: "Spark",
                price: "$4,999",
                cadence: "/ mo",
                extras: "1 channel · manages up to $15k / mo ad spend",
                positioning: "Validate one paid channel with real ICP data — before you commit budget.",
                timeToLive: "First campaign live in 10 days",
                highlight: false,
            },
            {
                id: "scale",
                name: "Scale",
                price: "$8,999",
                cadence: "/ mo",
                extras: "2 channels · manages $15k–$75k / mo · dedicated director",
                positioning: "Compound your working channel while opening the second one profitably.",
                timeToLive: "Second channel live in week 3",
                highlight: true,
            },
        ],
    },
    {
        id: "meme-trend-marketing",
        icon: Zap,
        eyebrow: "03",
        category: "growth",
        name: "Meme & Trend Marketing",
        tagline: "Be the brand joking with the trend — not the one reacting to it three weeks late.",
        description:
            "The internet moves in hours; most brand approval chains move in weeks. We monitor daily, ship reactive content in a same-day loop with a brand-safety layer, and place it on your handles or partnered meme pages with real reach. The compounding effect is free social distribution your competitors can't match.",
        salesExample: {
            before: "Follow us on social for product updates.",
            after: "Remember that Google Docs meme going around last Tuesday? That was us — 3.4M organic impressions, 40k new followers, zero paid spend.",
        },
        valueTagline: "Own the internet's fastest currency — attention that spreads for free while your competitors are still in approval calls.",
        deliverables: [
            "Daily trend monitoring (Reddit, X, TikTok, meme pages)",
            "Same-day reactive content — 24h turnaround",
            "Meme-page partnerships with negotiated CPMs",
            "Brand-safety review before every post",
            "Weekly virality report + repurpose plan",
        ],
        tiers: [
            {
                id: "pulse",
                name: "Pulse",
                price: "$1,999",
                cadence: "/ mo",
                extras: "8 reactive posts / month · your handles only",
                positioning: "Test whether reactive, culture-first content works for your brand this quarter.",
                timeToLive: "First reactive post live in 48h",
                highlight: false,
            },
            {
                id: "wave",
                name: "Wave",
                price: "$4,499",
                cadence: "/ mo",
                extras: "20 reactive posts + 4 meme-page placements / month",
                positioning: "Convert weekly virality into an owned distribution engine your competitors can't buy.",
                timeToLive: "First meme-page placement in week 2",
                highlight: true,
            },
        ],
    },
    // ===== Content cluster =====
    {
        id: "podcast-production",
        icon: Mic,
        eyebrow: "04",
        category: "content",
        name: "Podcast Production & Sponsorships",
        tagline: "Buy a permanent seat in your buyer's earbuds.",
        description:
            "Sponsor the shows your ICP already listens to every commute, or launch the one they'll obsess over. Full-stack production (recording, editing, distribution, guest booking) or curated host-read placements on 4+ industry shows a quarter. The channel with the highest trust-per-minute in marketing.",
        salesExample: {
            before: "Have you heard of our company?",
            after: "Your CEO probably listens to [that industry podcast] on their morning commute. We were the last three sponsors — and the host mentions us in every intro.",
        },
        valueTagline: "Buy monthly, unforgettable positioning inside your buyer's most trusted routine — no algorithm can take it away.",
        deliverables: [
            "Guest sourcing + booking (top-tier CEOs, founders, thinkers)",
            "Studio-quality remote recording + engineering",
            "Editing, chapters, transcripts, show notes",
            "Distribution across Spotify / Apple / YouTube",
            "Host-read sponsorship placements on 4+ industry shows / quarter",
            "Growth playbook (audience → newsletter → pipeline)",
        ],
        tiers: [
            {
                id: "guest",
                name: "Guest",
                price: "$3,499",
                cadence: "/ mo",
                extras: "2 episodes / month · your own show, we handle production",
                positioning: "Launch your own category show and land the first 1,000 core listeners.",
                timeToLive: "First episode live in 21 days",
                highlight: false,
            },
            {
                id: "network",
                name: "Network",
                price: "$6,999",
                cadence: "/ mo",
                extras: "4 episodes + host-read sponsorship placements",
                positioning: "Buy monthly, unforgettable recall inside your buyer's most trusted daily habit.",
                timeToLive: "First sponsorship live in week 3",
                highlight: true,
            },
        ],
    },
    {
        id: "case-study-library",
        icon: Video,
        eyebrow: "05",
        category: "content",
        name: "Case Study Video Library",
        tagline: "Close every deal 3× faster by letting your happiest customer do the pitching.",
        description:
            "Cinematic customer testimonial videos, batched to keep costs sane. We handle the concierge sourcing, on-location filming, and story-led edits (no talking-head slog). Sales teams close 3× faster with a good video testimonial in the deck.",
        salesExample: {
            before: "Here's our 12-page PDF case study from 2023.",
            after: "Watch this two-minute video of your competitor's former VP of Operations explaining exactly why they switched to us. I'll wait.",
        },
        deliverables: [
            "Customer concierge — we source and coordinate everything",
            "On-location or premium remote-studio filming",
            "Story-led editing (not talking-head slog)",
            "3 cuts per customer (60s / 3-min / long-form)",
            "Transcripts + captioned social cutdowns",
            "Sales-team distribution guide",
        ],
        tiers: [
            {
                id: "cohort",
                name: "Cohort",
                price: "$12,999",
                cadence: "/ project",
                extras: "4 customer videos batched over 6 weeks",
                positioning: "Arm your sales team with 4 fresh video testimonials — one shoot window, all edited.",
                timeToLive: "First cut delivered in 4 weeks",
                highlight: false,
            },
            {
                id: "retainer",
                name: "Retainer",
                price: "$4,999",
                cadence: "/ mo",
                extras: "1 fresh case study / month · always-on",
                positioning: "One new customer story every 30 days — a compounding sales-close library, forever.",
                timeToLive: "First case study delivered in month 1",
                highlight: true,
            },
        ],
    },
];

// Category metadata for rendering section headers between service groups.
const categoryOrder = [
    {
        id: "growth",
        eyebrow: "Growth · 01",
        label: "Grow the pipeline",
        blurb:
            "The three channels most brands can't run in-house. Creators, ads, and the trends moving the internet this week.",
    },
    {
        id: "content",
        eyebrow: "Content · 02",
        label: "Own the story",
        blurb:
            "Long-form assets your team will still be leaning on in three years. Podcasts your buyers already listen to, and case-study films that close deals for you.",
    },
];


/**
 * Renders a single service block with an expand toggle that keeps the
 * numeric "product SKU" details (deliverables, "6 UGC ads / month" etc.)
 * behind a click. Above the fold: outcome + salesman line + value hook.
 * Below the fold: what's actually in the box.
 */
function ServiceBlock({ s }) {
    const [expanded, setExpanded] = useState(false);
    const Icon = s.icon;
    return (
        <article
            data-testid={`service-block-${s.id}`}
            className="pb-16 border-b border-white/10 last:border-b-0"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                {/* Left: description */}
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-4 mb-6">
                        <span className="font-display text-bone-500 text-sm tracking-[0.22em]">
                            {s.eyebrow}
                        </span>
                        <span className="w-8 h-px bg-white/15" />
                        <Icon className="w-5 h-5 text-ember" />
                    </div>
                    <h3
                        data-testid={`service-title-${s.id}`}
                        className="font-display text-bone text-4xl lg:text-5xl tracking-tight leading-[1.02] mb-4"
                    >
                        {s.name}
                    </h3>
                    <p className="font-serif-i italic text-ember text-lg mb-6">
                        {s.tagline}
                    </p>
                    <p className="text-bone-300 text-base leading-relaxed max-w-[48ch]">
                        {s.description}
                    </p>

                    {s.salesExample && (
                        <div
                            data-testid={`sales-example-${s.id}`}
                            className="mt-8 p-5 lg:p-6 border-l-2 border-ember/60 bg-ink-800/50 max-w-[48ch]"
                        >
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-ember mb-4">
                                <Quote className="w-3.5 h-3.5" />
                                What your sales team says now
                            </div>
                            <div className="mb-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-bone-500 mb-1.5">
                                    Instead of
                                </div>
                                <p className="text-sm text-bone-500 italic line-through decoration-bone-500/40">
                                    &ldquo;{s.salesExample.before}&rdquo;
                                </p>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-ember mb-1.5">
                                    Your sales team says
                                </div>
                                <p className="text-sm text-bone leading-relaxed">
                                    &ldquo;{s.salesExample.after}&rdquo;
                                </p>
                            </div>
                        </div>
                    )}

                    {s.valueTagline && (
                        <p
                            data-testid={`value-tagline-${s.id}`}
                            className="mt-8 font-display text-bone text-lg lg:text-xl leading-snug tracking-tight max-w-[42ch]"
                        >
                            {s.valueTagline}
                        </p>
                    )}
                </div>

                {/* Right: tier cards + expand toggle */}
                <div className="lg:col-span-6 lg:col-start-7">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {s.tiers.map((t) => (
                            <div
                                key={t.id}
                                data-testid={`service-tier-${s.id}-${t.id}`}
                                className={`relative flex flex-col p-6 lg:p-7 border transition-colors ${
                                    t.highlight
                                        ? "border-ember/60 bg-ink-800/60"
                                        : "border-white/10 bg-ink-800/30"
                                }`}
                            >
                                {t.highlight && (
                                    <div className="absolute -top-3 left-6 px-2.5 py-1 bg-ember text-ink text-[10px] uppercase tracking-[0.2em] font-semibold">
                                        Most chosen
                                    </div>
                                )}
                                <div className="text-[10px] uppercase tracking-[0.22em] text-bone-500 mb-3">
                                    {t.name}
                                </div>
                                <div className="flex items-baseline gap-1.5 mb-6">
                                    <span
                                        data-testid={`tier-price-${s.id}-${t.id}`}
                                        className="font-display text-bone text-3xl lg:text-4xl tracking-tight"
                                    >
                                        {t.price}
                                    </span>
                                    <span className="text-bone-500 text-sm">
                                        {t.cadence}
                                    </span>
                                </div>
                                {t.positioning && (
                                    <p
                                        data-testid={`tier-positioning-${s.id}-${t.id}`}
                                        className="text-bone text-sm leading-relaxed mb-5 font-serif-i italic"
                                    >
                                        {t.positioning}
                                    </p>
                                )}
                                {t.timeToLive && (
                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-ember mb-5">
                                        <Clock className="w-3 h-3" />
                                        <span>{t.timeToLive}</span>
                                    </div>
                                )}
                                {expanded && t.extras && (
                                    <p className="text-bone-300 text-xs uppercase tracking-[0.16em] leading-relaxed mb-6 border-t border-white/10 pt-4">
                                        {t.extras}
                                    </p>
                                )}
                                <div className="flex-1" />
                                <Link
                                    to="/#contact"
                                    data-testid={`tier-cta-${s.id}-${t.id}`}
                                    className={`inline-flex items-center justify-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em] font-medium ${
                                        t.highlight
                                            ? "btn-ember"
                                            : "border border-white/15 hover:border-ember hover:text-ember transition-colors"
                                    }`}
                                >
                                    Choose {t.name}
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        ))}
                    </div>

                    {/* Expand toggle sits under the tier cards, right-column only. */}
                    <div className="mt-6 pt-6 border-t border-white/10">
                        <button
                            type="button"
                            data-testid={`expand-${s.id}`}
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ember hover:text-bone transition-colors"
                        >
                            <span>
                                {expanded
                                    ? "Hide what's inside"
                                    : "See what's actually included"}
                            </span>
                            <ChevronDown
                                className={`w-4 h-4 transition-transform duration-300 ${
                                    expanded ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        {expanded && (
                            <ul
                                data-testid={`deliverables-${s.id}`}
                                className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5"
                            >
                                {s.deliverables.map((d) => (
                                    <li
                                        key={d}
                                        className="flex items-start gap-2.5 text-sm text-bone-300"
                                    >
                                        <Check className="w-3.5 h-3.5 text-ember mt-1 shrink-0" />
                                        <span>{d}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}

const proof = [
    { stat: "3.4×", label: "median ROAS lift across paid clients, first 90 days" },
    { stat: "82%", label: "of retainers renew past year one" },
    { stat: "12k+", label: "creator-shot UGC assets shipped for clients to date" },
    { stat: "18h", label: "average time-to-live on a reactive meme placement" },
];

const process = [
    {
        n: "01",
        title: "Signal audit",
        body: "Two weeks reading your current spend, creator relationships, press footprint and social presence. What's actually working, what's underperforming, where the leverage is hiding. Deck yours to keep either way.",
    },
    {
        n: "02",
        title: "Weekly cadence",
        body: "Every week ships something — UGC concepts, creator briefs, ad iterations, pitches, essays, meme placements. Predictable output your team can plan around instead of hoping for.",
    },
    {
        n: "03",
        title: "Hypothesis-labelled tests",
        body: "Every ad, creator, post, pitch and placement carries a written hypothesis before it runs. Winners get scaled; losers get filed with a cause of death so the team learns instead of retrying.",
    },
    {
        n: "04",
        title: "Compounding readouts",
        body: "Weekly Looms, monthly written retros. By quarter two you have a documented model of your buyer + a growing library of earned coverage — the most valuable output of the engagement.",
    },
];

export default function PaidMedia() {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        document.title = "Growth · Reputation · Content — Aximo Studios";
        return () => {
            document.title = "Aximo Studios — Full-Service Creative Agency";
        };
    }, []);

    return (
        <main
            data-testid="paid-media-page"
            className="digtek-page min-h-screen bg-ink text-bone"
        >
            <DigtekNavbar />

            {/* Hero */}
            <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 grain">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ember mb-8">
                        <span className="block w-8 h-px bg-ember" />
                        Service · Growth
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8">
                            <h1
                                data-testid="paid-media-title"
                                className="font-display font-medium tracking-tightest text-bone text-6xl sm:text-7xl lg:text-[110px] leading-[0.94]"
                            >
                                Become the brand
                                <br />
                                <span className="text-bone-500">
                                    your category
                                </span>{" "}
                                <span className="font-serif-i italic font-normal text-ember">
                                    can&rsquo;t ignore.
                                </span>
                            </h1>
                            <p className="mt-8 text-bone-300 text-lg lg:text-xl max-w-[62ch] leading-relaxed">
                                Five ways to turn strangers into buyers — creator-shot
                                ads, paid promotions, meme &amp; trend content, podcast
                                sponsorships and case-study films. All run under one
                                operating rhythm by one senior team. Less noise. Tighter
                                narratives. Sharper measurement.
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:justify-self-end space-y-3 text-sm text-bone-300">
                            <div className="flex items-center gap-2">
                                <Flame className="w-4 h-4 text-ember" />
                                <span>Retainers open for Q2 2026 · 4 seats left</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Target className="w-4 h-4 text-ember" />
                                <span>Fixed fee · never % of spend</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <LineChart className="w-4 h-4 text-ember" />
                                <span>Full ownership of ad accounts & rights, always</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Proof strip */}
            <section
                data-testid="paid-media-proof"
                className="border-y border-white/10 bg-ink-900 py-12 lg:py-16 grain"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-8">
                    {proof.map((p, i) => (
                        <div
                            key={p.stat + "-" + p.label}
                            data-testid={`paid-media-proof-${i}`}
                            className="px-4"
                        >
                            <div className="font-display text-ember text-4xl lg:text-6xl tracking-tightest leading-none mb-3">
                                {p.stat}
                            </div>
                            <div className="text-bone-300 text-xs uppercase tracking-[0.18em] max-w-[26ch]">
                                {p.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Service sections */}
            <section
                data-testid="paid-media-services"
                id="services"
                className="py-24 lg:py-32"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-20">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ember mb-6">
                                <span className="block w-8 h-px bg-ember" />
                                Five services · two clusters · one team
                            </div>
                            <h2 className="font-display text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] max-w-[20ch]">
                                Priced by the problem you&rsquo;re solving — not the hours we spend.
                            </h2>
                        </div>
                        <p className="lg:col-span-4 lg:col-start-9 text-bone-300 text-base leading-relaxed">
                            Every engagement is fixed-fee — never a percentage of your ad
                            or influencer spend. Our incentive is to solve the problem
                            faster, not to convince you to spend more on the symptom.
                        </p>
                    </div>

                    <div className="space-y-24 lg:space-y-32">
                        {categoryOrder.map((cat) => {
                            const items = services.filter((s) => s.category === cat.id);
                            if (items.length === 0) return null;
                            return (
                                <div
                                    key={cat.id}
                                    data-testid={`service-cluster-${cat.id}`}
                                >
                                    {/* Cluster header */}
                                    <div className="mb-12 lg:mb-16 pb-8 border-b border-white/15 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
                                        <div className="lg:col-span-7">
                                            <div className="text-[10px] uppercase tracking-[0.28em] text-ember mb-3">
                                                {cat.eyebrow}
                                            </div>
                                            <h3
                                                data-testid={`cluster-label-${cat.id}`}
                                                className="font-display text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.02]"
                                            >
                                                {cat.label}
                                            </h3>
                                        </div>
                                        <p className="lg:col-span-4 lg:col-start-9 text-bone-300 text-sm leading-relaxed">
                                            {cat.blurb}
                                        </p>
                                    </div>

                                    {/* Service items in this cluster */}
                                    <div className="space-y-16 lg:space-y-24">
                                        {items.map((s) => (
                                            <ServiceBlock key={s.id} s={s} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section
                data-testid="paid-media-process"
                className="py-24 lg:py-32 bg-ink-900 border-y border-white/10 grain"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ember mb-6">
                                <span className="block w-8 h-px bg-ember" />
                                How we run growth
                            </div>
                            <h2 className="font-display text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] max-w-[18ch]">
                                Every week is a labeled experiment, not a status update.
                            </h2>
                        </div>
                        <p className="lg:col-span-4 lg:col-start-9 text-bone-300 text-base leading-relaxed">
                            The output isn&rsquo;t the ads or the posts. It&rsquo;s the
                            model of your buyer that emerges after twelve weeks of
                            structured cohorts.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {process.map((p) => (
                            <div
                                key={p.n}
                                data-testid={`paid-process-${p.n}`}
                                className="bg-ink p-8 lg:p-10"
                            >
                                <div className="flex items-baseline justify-between mb-6">
                                    <span className="font-display text-bone-500 text-sm tracking-[0.2em]">
                                        {p.n}
                                    </span>
                                    <Layers className="w-4 h-4 text-bone-500" />
                                </div>
                                <h3 className="font-display text-bone text-2xl lg:text-3xl tracking-tight mb-4">
                                    {p.title}
                                </h3>
                                <p className="text-bone-300 text-sm leading-relaxed max-w-[52ch]">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-6">
                        Next step
                    </div>
                    <h2 className="font-display text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-balance">
                        Book a 30-minute signal audit.
                        <br />
                        <span className="text-bone-500">
                            Free, whether you sign or not.
                        </span>
                    </h2>
                    <p className="mt-8 text-bone-300 text-base lg:text-lg max-w-[52ch] mx-auto">
                        Share account and social access; two weeks later you get a deck
                        reading your growth stack like a novel. What&rsquo;s working,
                        what isn&rsquo;t, and where the leverage is hiding. Yours to
                        keep either way.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/#contact"
                            data-testid="paid-media-cta-primary"
                            className="inline-flex items-center gap-2 btn-ember h-14 px-8 text-sm font-medium tracking-wide"
                        >
                            Request a signal audit
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/#pricing"
                            data-testid="paid-media-cta-secondary"
                            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-bone-300 hover:text-ember transition-colors"
                        >
                            Compare with our creative retainers
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <DigtekFooter />
        </main>
    );
}
